/**
 * Client-side card checks for the payment page. They mirror the API's
 * (sw-ev-api parseCard) so an obvious typo is caught before the card
 * leaves the device; the API's answer still decides.
 */

export const digitsOf = (value) => String(value ?? '').replace(/\D/g, '');

export function luhnOk(number) {
  const digits = digitsOf(number);
  if (!digits) return false;
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let d = digits.charCodeAt(i) - 48;
    if (double) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    double = !double;
  }
  return sum % 10 === 0;
}

/**
 * @param {{number: string, expiry: string, cvv: string}} fields - as typed (formatted).
 * @returns {{ok: true, card: {number, expMonth, expYear, cvv}} | {ok: false, errors: {number?, expiry?, cvv?}}}
 */
export function validateCard(fields, now = new Date()) {
  const errors = {};
  const number = digitsOf(fields?.number);
  if (!/^\d{13,19}$/.test(number) || !luhnOk(number)) {
    errors.number = 'Enter a valid card number.';
  }

  const expiry = digitsOf(fields?.expiry);
  const month = expiry.slice(0, 2);
  const year = expiry.slice(2, 4);
  if (expiry.length !== 4 || !/^(0[1-9]|1[0-2])$/.test(month)) {
    errors.expiry = 'Enter the expiry date as MM/YY.';
  } else if ((2000 + Number(year)) * 12 + Number(month) < now.getFullYear() * 12 + now.getMonth() + 1) {
    // A card is valid through the last day of its expiry month.
    errors.expiry = 'This card has expired.';
  }

  const cvv = digitsOf(fields?.cvv);
  if (!/^\d{3,4}$/.test(cvv) || cvv !== String(fields?.cvv ?? '').trim()) {
    errors.cvv = 'Enter the 3 or 4 digit security code.';
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors };
  return { ok: true, card: { number, expMonth: month, expYear: year, cvv } };
}
